import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Users, Rocket, Heart, Target, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { icon: Users, title: "Every citizen counts", body: "From a corner-shop owner to an MNC hiring 10,000 engineers — everyone deserves visibility, income and dignity." },
  { icon: Heart, title: "Vocal for Local", body: "We amplify neighbourhood workers, tiny shops, and honest labour. The best economy is a hyper-local economy." },
  { icon: Target, title: "One roof, all needs", body: "Services, shopping, homes, jobs, healthcare, government schemes, safety, emergencies — the whole of daily life inside one app." },
  { icon: Rocket, title: "Real-time trust", body: "Live map tracking, verified profiles, transparent ratings, and an auditable IVR-style communication trail between every buyer and seller." },
];

const VISION_BULLETS = [
  "A citizen in any village or city finds a plumber, doctor, cab, or job in under 30 seconds — with live tracking.",
  "Every small shop, worker, and labourer earns online without paying middlemen or ad platforms.",
  "Government schemes reach the last citizen because they are one tap away, not one form-fill away.",
  "In a crisis — accident, fire, robbery, medical — the whole neighbourhood is alerted before the siren arrives.",
];

export default function About() {
  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
      {/* Hero */}
      <section className="grid lg:grid-cols-12 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#2D5A27] text-white px-3 py-1 text-xs font-extrabold uppercase tracking-[0.18em]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#DDA77B]" /> Vocal · For · Local
          </div>
          <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 leading-[1.05] mt-6">
            One roof for<br />
            <span className="text-[#E05E36]">every citizen,</span><br />
            <span className="italic underline decoration-[#DDA77B] decoration-[6px] underline-offset-4">every need.</span>
          </h1>
          <p className="mt-5 text-stone-600 text-base sm:text-lg max-w-xl">
            find2near was founded with a single conviction: the local shopkeeper, the labourer, the doctor, and the fresh graduate all deserve to be found — instantly, by their neighbours — without ad budgets or middlemen.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/post"><Button className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2">Post your work <ArrowRight size={16} /></Button></Link>
            <Link to="/citizens"><Button variant="outline" className="rounded-full">Meet neighbours</Button></Link>
          </div>
        </motion.div>

        {/* Founder card */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15, duration: 0.5 }} className="lg:col-span-5">
          <div className="relative rounded-3xl bg-white border border-stone-200 p-6 md:p-8 shadow-sm overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#E05E36]/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-[#2D5A27]/12 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#E05E36] to-[#c94e2a] text-white grid place-items-center heading font-black text-3xl shadow-lg">
                  PA
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Founder &amp; CEO</div>
                  <div className="heading text-2xl font-extrabold text-stone-900" data-testid="founder-name">Pushkar Agarwal</div>
                  <div className="text-sm text-[#2D5A27] font-semibold">Patentee · Entrepreneur · Community builder</div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-stone-100 space-y-3 text-sm text-stone-700 leading-relaxed">
                <p><b className="text-stone-900">"Every worker, every shop, every dream — nearby."</b></p>
                <p>Pushkar started find2near to close the gap between hardworking locals and the neighbours who need them. His conviction: technology should not exclude the elderly shopkeeper, the daily-wage worker, or the small-town graduate — it should be the fastest ladder for them.</p>
                <p>His playbook: build one honest, transparent super-app where the auto driver, the plumber, the doctor, the recruiter, and the government all meet the citizen — with zero cash burn, zero middlemen, and full location-based trust.</p>
              </div>
              <div className="mt-6 flex items-center gap-3 text-xs text-stone-500 pt-4 border-t border-stone-100">
                <Sparkles size={14} className="text-[#DDA77B]" />
                <span>Building find2near to serve <b className="text-stone-800">1 billion citizens</b> under one roof.</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Vision */}
      <section className="mt-20">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">The vision</div>
        <h2 className="heading text-3xl md:text-5xl font-extrabold text-stone-900 mt-2">What Pushkar sees</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {VISION_BULLETS.map((v, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="bg-white border border-stone-200 rounded-2xl p-6 flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#E05E36] text-white grid place-items-center heading font-black shrink-0">{i + 1}</div>
              <p className="text-stone-700 leading-relaxed">{v}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pillars */}
      <section className="mt-20">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Our pillars</div>
        <h2 className="heading text-3xl md:text-5xl font-extrabold text-stone-900 mt-2">Four beliefs · one mission</h2>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white border border-stone-200 rounded-2xl p-5">
                <div className="w-11 h-11 rounded-xl bg-[#FFF3EC] text-[#E05E36] grid place-items-center mb-3"><Icon size={22} /></div>
                <div className="heading font-bold text-stone-900">{p.title}</div>
                <p className="text-sm text-stone-600 mt-1">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-20">
        <div className="relative overflow-hidden rounded-3xl bg-[#2D5A27] text-white p-8 md:p-14 grid md:grid-cols-2 gap-8 items-center">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#E05E36]/30 blur-2xl" />
          <div className="relative">
            <Logo size={44} showText={false} />
            <h3 className="heading text-3xl md:text-5xl font-extrabold mt-4">Join the founder's mission.</h3>
            <p className="mt-4 text-white/85 max-w-md">Post your work, discover neighbours, and be part of the Vocal-for-Local movement Pushkar started.</p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/account" className="inline-flex items-center gap-2 bg-white text-[#2D5A27] font-bold px-5 py-3 rounded-full hover:scale-105 transition-transform" data-testid="about-create-profile">
                Create your profile <ArrowRight size={18} />
              </Link>
              <Link to="/search" className="inline-flex items-center gap-2 bg-white/10 border border-white/30 font-bold px-5 py-3 rounded-full">
                Explore neighbourhood
              </Link>
            </div>
          </div>
          <div className="relative grid grid-cols-2 gap-4">
            {[
              { n: "14", t: "categories under one roof" },
              { n: "24×7", t: "live map tracking" },
              { n: "10+", t: "emergency helplines" },
              { n: "0", t: "middlemen. ever." },
            ].map(s => (
              <div key={s.t} className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5">
                <div className="heading text-3xl font-extrabold">{s.n}</div>
                <div className="text-white/70 text-sm">{s.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
